import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const projects = [
  {
    n: "01",
    slug: "kickz",
    title: "K!ckz",
    role: "UI/UX & Brand Design",
    year: "2026",
    tags: ["Mobile App", "E-commerce", "Sneakers"],
    blurb:
      "A sneaker shopping app built around drop culture — browse, buy and track limited releases without ever missing a drop. Bold sticker branding meets a fast, card-driven shopping flow.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/0fe3xprx_kickz%20app%20mockup.jpg",
    span: "md:col-span-6",
    height: "h-[420px] md:h-[560px]",
  },
  {
    n: "02",
    slug: "sayso",
    title: "SaySo",
    role: "UI/UX & Brand Design",
    year: "2026",
    tags: ["Mobile App", "Social", "Women-Centered"],
    blurb:
      "A women-centered social app for asking advice, running text & image polls and planning things with friends. A soft, private space between a group chat, a diary and a community — no followers, no chase for likes.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/rbjz2wot_mock2.jpg",
    span: "md:col-span-6",
    height: "h-[420px] md:h-[560px]",
  },
  {
    n: "03",
    slug: "the-present",
    title: "The Present",
    role: "UI/UX & Brand Design",
    year: "2025",
    tags: ["Mobile App", "Gifting"],
    blurb:
      "A gift-recommendation app that ends gift-stress. A short guided quiz turns 'no idea what to buy' into a curated shortlist of presents they'll actually love.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/1okp2cgs_the%20present.jpg",
    span: "md:col-span-6",
    height: "h-[420px] md:h-[560px]",
  },
  {
    n: "04",
    slug: "rarefig",
    title: "Rarefig",
    role: "UI/UX, Brand & Illustration",
    year: "2025",
    tags: ["Website", "E-commerce", "Collectibles"],
    blurb:
      "A retro-flavoured webshop for collectible figures — Funko Pops, minifigures and statues — complete with grading service, layered filters and a mascot dragon guarding the shelves.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/0yj2rcb6_rarefig.jpg",
    span: "md:col-span-6",
    height: "h-[420px] md:h-[560px]",
  },
];

export default function Work() {
  const navigate = useNavigate();
  const KLEIN = "#0851F2";

  return (
    <section
      id="work"
      className="relative py-24 md:py-40 border-t border-[var(--ink)] bg-white overflow-hidden"
      data-testid="work-section"
    >
      {/* Klein-blue backdrop shapes — pushed to L/R margins so content reads cleanly */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        {/* LEFT margin */}
        <svg
          className="absolute"
          style={{ left: "-90px", top: "8%", width: "280px", height: "280px", opacity: 0.22 }}
          viewBox="0 0 280 280"
          fill="none"
        >
          <circle cx="140" cy="140" r="130" stroke={KLEIN} strokeWidth="3" />
        </svg>
        <span
          className="absolute"
          style={{
            left: "1%",
            top: "30%",
            width: "120px",
            height: "120px",
            background: KLEIN,
            opacity: 0.14,
            transform: "rotate(-14deg)",
          }}
        />
        <span
          className="absolute"
          style={{
            left: "2%",
            top: "52%",
            width: "32px",
            height: "32px",
            background:
              `linear-gradient(${KLEIN},${KLEIN}) center/100% 4px no-repeat,` +
              `linear-gradient(${KLEIN},${KLEIN}) center/4px 100% no-repeat`,
            opacity: 0.6,
          }}
        />
        <span
          className="absolute"
          style={{
            left: "2%",
            bottom: "12%",
            width: "150px",
            height: "200px",
            border: `2.5px solid ${KLEIN}`,
            opacity: 0.24,
            transform: "rotate(7deg)",
          }}
        />
        {[
          { top: "78%", s: 10 },
          { top: "81%", s: 7 },
          { top: "84%", s: 9 },
        ].map((d, i) => (
          <span
            key={`wl-${i}`}
            className="absolute"
            style={{
              left: "5%",
              top: d.top,
              width: `${d.s}px`,
              height: `${d.s}px`,
              borderRadius: "9999px",
              background: KLEIN,
              opacity: 0.55,
            }}
          />
        ))}

        {/* RIGHT margin */}
        <svg
          className="absolute"
          style={{ right: "1%", top: "8%", width: "260px", height: "60px", opacity: 0.4 }}
          viewBox="0 0 260 60"
          fill="none"
        >
          <path
            d="M 5 30 Q 35 5, 65 30 T 125 30 T 185 30 T 250 30"
            stroke={KLEIN}
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
        <svg
          className="absolute"
          style={{ right: "-60px", top: "30%", width: "260px", height: "260px", opacity: 0.22 }}
          viewBox="0 0 260 260"
          fill="none"
        >
          <circle cx="130" cy="130" r="120" stroke={KLEIN} strokeWidth="2.5" />
          <circle cx="130" cy="130" r="78" stroke={KLEIN} strokeWidth="2.5" />
          <circle cx="130" cy="130" r="36" stroke={KLEIN} strokeWidth="2.5" />
        </svg>
        <svg
          className="absolute"
          style={{ right: "2%", top: "62%", width: "160px", height: "150px", opacity: 0.28 }}
          viewBox="0 0 160 150"
          fill="none"
        >
          <polygon points="80,10 150,140 10,140" stroke={KLEIN} strokeWidth="3" />
        </svg>
        <span
          className="absolute"
          style={{
            right: "3%",
            bottom: "8%",
            width: "90px",
            height: "90px",
            background: KLEIN,
            opacity: 0.16,
            transform: "rotate(18deg)",
          }}
        />
      </div>

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-20 md:mb-28">
          <div className="md:col-span-3">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
              §02 — UI/UX Case Studies
            </span>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-sans-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.03em] font-light uppercase">
              UI/UX
              <br />
              <span className="font-display italic lowercase font-light">
                selected works
              </span>
            </h2>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--ink-soft)]">
              Each case study lives in its own world — click through to enter.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
          {projects.map((p, i) => (
            <motion.article
              key={p.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              className={`group ${p.span} cursor-none`}
              data-testid={`work-card-${p.slug}`}
              onClick={() => navigate(`/work/${p.slug}`)}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && navigate(`/work/${p.slug}`)}
            >
              <div
                className={`relative overflow-hidden ${p.height} bg-[var(--paper)] border border-[var(--ink)]`}
              >
                <img
                  src={p.img}
                  alt={p.title}
                  className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                />
                <div
                  className="absolute top-4 left-4 font-sans-display uppercase text-[80px] md:text-[140px] leading-none text-white"
                  style={{
                    textShadow: "0 3px 12px rgba(0,0,0,0.35), 0 0 2px rgba(0,0,0,0.5)",
                  }}
                >
                  {p.n}
                </div>
                <div className="absolute top-5 right-5 flex flex-wrap justify-end gap-2 max-w-[60%]">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 bg-white/85 backdrop-blur text-[var(--ink)]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 bg-gradient-to-t from-black/65 to-transparent">
                  <div className="flex items-end justify-between gap-4">
                    <h3 className="font-display text-2xl md:text-4xl text-white">
                      {p.title}
                    </h3>
                    <ArrowUpRight className="w-8 h-8 text-white transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                  <div className="mt-2 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/85">
                    <span>{p.role}</span>
                    <span>·</span>
                    <span>{p.year}</span>
                  </div>
                </div>
              </div>
              <div className="mt-5 flex items-start justify-between gap-6">
                <p className="text-[var(--ink-soft)] text-base md:text-lg leading-relaxed max-w-[60ch]">
                  {p.blurb}
                </p>
                <span className="hidden md:inline-block shrink-0 font-mono text-[11px] uppercase tracking-[0.25em] border border-[var(--ink)] px-4 py-3 group-hover:bg-[var(--ink)] group-hover:text-white transition-all duration-500">
                  Read case study ↗
                </span>
              </div>
            </motion.article>
          ))}
        </div>

        {/* CTA row */}
        <div className="mt-20 md:mt-28 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-t border-[var(--line)] pt-10">
          <p className="font-display italic text-3xl md:text-4xl max-w-[28ch]">
            Three apps, one website — and a shelf full of graphic work below.
          </p>
          <button
            className="font-mono text-[11px] uppercase tracking-[0.25em] px-6 py-4 border border-[var(--ink)] hover:bg-[var(--ink)] hover:text-white transition-all duration-500"
            data-testid="work-cta-graphic"
            onClick={(e) => {
              e.stopPropagation();
              document
                .getElementById("graphic")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            See graphic design ↓
          </button>
        </div>
      </div>
    </section>
  );
}
